import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { SelectDropdown } from '~/components/ui/SelectDropdown';
import { TextLabel } from '../TextLabel';
import { ISelectItem } from './Select.model';
import {
	StyledContainer,
	StyledDropdownContainer,
	StyledSelectTopContainer,
} from './Select.styled';

const Select = ({
	value,
	items,
	placeholder,
	onChange,
	icon,
	dropdownHeight,
	...props
}: {
	value: string | number | null;
	items: ISelectItem[];
	placeholder: string;
	onChange: (value: ISelectItem) => void;
	icon: string;
	dropdownHeight?: string;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>) => {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const ref = useRef<HTMLDivElement>(null);

	const selectedOption = useMemo(
		() => items.find((item) => item.value === value),
		[items, value],
	);

	const handleSelect = useCallback(
		(item: ISelectItem) => {
			onChange(item);
			setIsOpen(false);
		},
		[onChange],
	);

	useEffect(() => {
		function handleClickOutside(event: Event) {
			if (ref.current && !ref.current.contains(event.target as Node)) {
				if (isOpen) {
					setIsOpen(false);
				}
			}
		}

		setTimeout(() => {
			document.addEventListener('click', handleClickOutside);
		}, 300);

		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, [ref, isOpen]);

	return (
		<StyledContainer {...props}>
			<StyledSelectTopContainer
				$active={!!value}
				$open={isOpen}
				$icon={icon}
				onClick={() => setIsOpen((prevState) => !prevState)}
				className={'value'}
			>
				<div className={'icon'} />
				<TextLabel>
					{selectedOption ? selectedOption.title : placeholder}
				</TextLabel>
				<div className={'arrow'} />
			</StyledSelectTopContainer>
			<CSSTransition
				in={isOpen}
				classNames={'results'}
				timeout={500}
				unmountOnExit
			>
				<StyledDropdownContainer>
					<SelectDropdown
						value={selectedOption?.title || ''}
						items={items.map((item) => item.title)}
						handleSelect={(title: string) => {
							const item = items.find((item) => item.title === title);
							if (item) {
								handleSelect(item);
							}
						}}
						visible={isOpen}
						dropdownHeight={dropdownHeight}
					/>
				</StyledDropdownContainer>
			</CSSTransition>
		</StyledContainer>
	);
};

export { Select };
